import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";

import { useLargeReportConfirm } from "src/hooks/useLargeReportConfirm";
import { useReportRangeLimit } from "src/hooks/useReportRangeLimit";

import { ActionButton, CardLayout, SelectField } from "../v3";
import { ProjectService } from "../../services";
import { projectReportValidation } from "../../pages/report/validation";
import { getFilteredOptions, PROJECT_TYPES, selectAllOption } from "../../utils";
import { ReportDateRangeFields } from "./ReportDateRangeFields.component";

export const ProjectReportForm = ({ reportType = "summary", handleClear = () => {}, onGenerate = () => {} }) => {
  const { months: maxRangeMonths } = useReportRangeLimit();
  const { confirmLargeRange, largeRangeDialog } = useLargeReportConfirm();
  const [selectedProjects, setSelectedProjects] = useState([selectAllOption]);
  const [selectedProjectTypes, setSelectedProjectTypes] = useState([selectAllOption]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [reportDetails, setReportDetails] = useState({
    projectIds: null,
    projectTypes: null,
    fromDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
  });

  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");

  useEffect(() => {
    if (!projectId) return;

    const fetchProject = async () => {
      try {
        const { data: project } = await ProjectService.getProjectById(projectId);
        setSelectedProjects([project]);
        setReportDetails((prev) => ({
          ...prev,
          projectIds: [project.id],
        }));
        setErrors((prev) => ({ ...prev, projectIds: "" }));
      } catch (error) {
        toast.error("Failed to fetch project details");
        console.error("🚀 ~ fetchProject ~ error:", error);
      }
    };

    fetchProject();
  }, [projectId]);

  useEffect(() => {
    handleClear();
    setLoading(false);
  }, [reportType]);

  const updateDetailsState = (event) => {
    setLoading(false);
    handleClear();
    const { name, value } = event.target;
    setReportDetails((prev) => ({ ...prev, [name]: value }));
    if (name === "fromDate") setErrors((prev) => ({ ...prev, fromDate: "" }));
    else if (name === "toDate") setErrors((prev) => ({ ...prev, toDate: "" }));
  };

  const handleProjectChange = (projects) => {
    setLoading(false);
    handleClear();
    const allSelected = projects.some((project) => project.id === "all");

    if (allSelected) {
      setSelectedProjects([selectAllOption]);
      setReportDetails((prev) => ({ ...prev, projectIds: null }));
    } else {
      // remove "all" if previously selected
      const filtered = projects.filter((project) => project.id !== "all");
      setSelectedProjects(filtered);
      setReportDetails((prev) => ({ ...prev, projectIds: filtered.map((project) => project.id) }));
    }

    setErrors((prev) => ({ ...prev, projectIds: "" }));
  };

  const handleTypeChange = (selected) => {
    setLoading(false);
    handleClear();
    const allSelected = selected.some((type) => type.id === "all");

    if (allSelected) {
      setSelectedProjectTypes([selectAllOption]);
      setReportDetails((prev) => ({ ...prev, projectTypes: null }));
    } else {
      const filtered = selected.filter((type) => type.id !== "all");
      setSelectedProjectTypes(filtered);
      setReportDetails((prev) => ({ ...prev, projectTypes: filtered.map((type) => type.value) }));
    }

    setErrors((prev) => ({ ...prev, projectTypes: "" }));
  };

  const getProjectTypeOptions = () =>
    Promise.resolve({
      data: PROJECT_TYPES,
    });

  const handleGenerateReport = async () => {
    setErrors({});
    const errors = projectReportValidation(reportDetails, selectedProjects[0]?.id, selectedProjectTypes[0]?.id, maxRangeMonths);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    if (!(await confirmLargeRange(reportDetails.fromDate, reportDetails.toDate))) return;

    try {
      setLoading(true);
      // Convert dates to ISO 8601 format
      const isoFromDate = moment(reportDetails.fromDate).startOf("day").toISOString();
      const isoToDate = moment(reportDetails.toDate).endOf("day").toISOString();

      const payload = {
        ...reportDetails,
        fromDate: isoFromDate,
        toDate: isoToDate,
        reportType,
      };

      onGenerate?.(payload, {
        onError: () => setLoading(false),
      });
    } catch (error) {
      setLoading(false);
      toast.error("Failed to generate report");
      console.error("🚀 ~ handleGenerateReport ~ error:", error);
    }
  };

  return (
    <>
      <CardLayout>
        <Box mt={1}>
          <Box display="flex" gap={2}>
            <SelectField
              label="Project"
              value={selectedProjects}
              getData={ProjectService.getProjectList}
              getDataStaticFilters={{ sortBy: "name", sortOrder: "asc" }}
              disabled={!!projectId}
              required
              multiSelect
              error={!!errors.projectIds}
              helperText={errors.projectIds}
              onChange={handleProjectChange}
              optionFilter={(options) => getFilteredOptions(options, selectedProjects, selectAllOption)}
              style={{ width: "100%" }}
            />
            <SelectField
              label="Project Type"
              value={selectedProjectTypes}
              getData={getProjectTypeOptions}
              required
              multiSelect
              error={!!errors.projectTypes}
              helperText={errors.projectTypes}
              onChange={handleTypeChange}
              optionFilter={(options) => getFilteredOptions(options, selectedProjectTypes, selectAllOption)}
              style={{ width: "100%" }}
            />
          </Box>
          <ReportDateRangeFields
            fromDate={reportDetails.fromDate}
            toDate={reportDetails.toDate}
            onChange={updateDetailsState}
            errors={errors}
          />
          {largeRangeDialog}
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <ActionButton buttonText="Generate" color="primary" disabled={loading} onButtonClick={handleGenerateReport} />
        </Box>
      </CardLayout>
    </>
  );
};
